import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AlertCircle, PlayCircle, Check, ArrowLeft } from 'lucide-react';
import { theme } from '../styles/theme';

const Container = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  
  @media (max-width: 768px) {
    margin: 0 20px;
    max-width: calc(100% - 40px);
    padding: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  position: relative;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  backdrop-filter: blur(16px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.2);
  }
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  margin: 0;
  flex: 1;
  text-align: center;
  font-weight: 600;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  text-align: center;
  margin: 0 0 2rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: white;
  font-size: 14px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  
  &::after {
    content: ' *';
    color: #fbbf24;
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  color: #1f2937;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #6366f1;
`;

const CheckboxLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.5;
  cursor: pointer;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const RequiredText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  margin: -8px 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const Button = styled.button`
  padding: 16px 24px;
  background: rgba(99, 102, 241, 0.9);
  backdrop-filter: blur(20px);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
    border-color: rgba(79, 70, 229, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  }
  
  &:disabled {
    background: rgba(209, 213, 219, 0.6);
    color: rgba(107, 114, 128, 0.8);
    border-color: rgba(209, 213, 219, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;


const HintText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  text-align: center;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const SavedInfoCard = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(139, 92, 246, 0.08) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1);
`;

const SavedInfoContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SavedInfoText = styled.div`
  color: white;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 4px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  p {
    font-size: 14px;
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 12px 20px;
    width: 100%;
  }
`;

const ContinueButton = styled(Button)`
  background: rgba(16, 185, 129, 0.9);
  border-color: rgba(16, 185, 129, 0.4);
  width: 100%;
  
  &:hover:not(:disabled) {
    background: rgba(5, 150, 105, 0.9);
    border-color: rgba(5, 150, 105, 0.6);
  }
`;

interface FormData {
  name: string;
  email: string;
}

interface Props {
  onSubmit: (data: FormData) => void;
  onBack?: () => void;
}

const STORAGE_KEY = 'yobi_fantasy_user_info';

export const LeadCaptureForm: React.FC<Props> = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: ''
  });
  const [savedUserInfo, setSavedUserInfo] = useState<FormData | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Check for saved user info on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setSavedUserInfo(parsedData);
        setFormData(parsedData);
      } catch (error) {
        // Silently handle parse error
      }
    } else {
      setShowForm(true);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const sendToWebhook = async (data: FormData) => {
    try {
      console.log('Sending form data to webhook:', data);

      const webhookData = {
        full_name: data.name,
        email: data.email
      };

      const response = await fetch('https://forms.yib.io/api/forms/submit/e0bCtlTy-d', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData),
      });

      if (response.ok) {
        const result = await response.text();
        console.log('Webhook submission successful:', result);
      } else {
        console.error('Webhook submission failed:', response.status, response.statusText);
      }
      
    } catch (error) {
      console.error('Webhook submission error:', error);
      // Don't block the user flow if webhook fails
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    
    // Send to webhook
    await sendToWebhook(formData);
    
    // Call the parent's onSubmit
    onSubmit(formData);
  };

  const handleUseSaved = async () => {
    if (savedUserInfo) {
      // Send to webhook (in case it's a new session)
      await sendToWebhook(savedUserInfo);
      
      // Use the saved info
      onSubmit(savedUserInfo);
    }
  };

  const handleUpdateInfo = () => {
    setShowForm(true);
  };

  const handleClearInfo = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSavedUserInfo(null);
    setFormData({
      name: '',
      email: ''
    });
    setShowForm(true);
  };
  

  const isValid = formData.name && formData.email;

  // Show saved user info card if available and not updating
  if (savedUserInfo && !showForm) {
    return (
      <Container>
        <Header>
          {onBack && (
            <BackButton onClick={onBack} aria-label="Go back">
              <ArrowLeft size={20} />
            </BackButton>
          )}
          <Title>Welcome Back!</Title>
          <div style={{ width: '40px' }} /> {/* Spacer for centering */}
        </Header>
        <Subtitle>We have your information saved from last time</Subtitle>
        
        <SavedInfoCard>
          <SavedInfoContent>
            <Check size={24} style={{ color: '#10b981' }} />
            <SavedInfoText>
              <h3>Using saved information</h3>
              <p>{savedUserInfo.name} • {savedUserInfo.email}</p>
            </SavedInfoText>
          </SavedInfoContent>
          <ButtonContainer>
            <ActionButton onClick={handleUpdateInfo}>
              Update Info
            </ActionButton>
            <ActionButton onClick={handleClearInfo}>
              Clear
            </ActionButton>
          </ButtonContainer>
        </SavedInfoCard>

        <ContinueButton onClick={handleUseSaved}>
          <PlayCircle size={20} />
          Speak to your AI Football Expert
        </ContinueButton>
        
        <HintText>
          <AlertCircle size={14} />
          5 minute limit
        </HintText>
      </Container>
    );
  }

  // Show the form
  return (
    <Container>
      <Header>
        {onBack && (
          <BackButton onClick={onBack} aria-label="Go back">
            <ArrowLeft size={20} />
          </BackButton>
        )}
        <Title>{savedUserInfo ? 'Update Your Information' : 'Almost There!'}</Title>
        <div style={{ width: '40px' }} /> {/* Spacer for centering */}
      </Header>
      <Subtitle>Enter your information to speak with your AI expert</Subtitle>
      
      <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="John Doe"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </InputGroup>

        <RequiredText>
          <AlertCircle size={14} />
          Both fields are required
        </RequiredText>

        <Button type="submit" disabled={!isValid}>
          <PlayCircle size={20} />
          {savedUserInfo ? 'Update & Start Call' : 'Start the Call'}
        </Button>
        
        {savedUserInfo && (
          <ActionButton 
            type="button" 
            onClick={() => setShowForm(false)}
            style={{ width: '100%', marginTop: '8px' }}
          >
            Cancel
          </ActionButton>
        )}
        
        <HintText>
          <AlertCircle size={14} />
          5 minute limit
        </HintText>
      </Form>
    </Container>
  );
};
